// LazyArt — оболочка для ДЕКОРАТИВНОГО lazy-модуля (иллюстрация публичной
// страницы, схема, кадр лендинга).
//
// Зачем отдельно от `LazyRouteBoundary`. Секции лендинга держали иллюстрации в
// голом `<Suspense>`: чанк картинки не загрузился — ошибка всплывала мимо всех
// границ до маршрута, и вместо страницы человек видел «Не удалось загрузить
// экран». Реальный случай (2026-08-31): вкладка полежала в фоне, сеть при
// пробуждении переключилась (`net::ERR_NETWORK_CHANGED`), два чанка-картинки
// не доехали — и лендинг умер целиком, хотя весь его смысл (текст, тарифы,
// кнопка входа) уже был отрисован.
//
// Правило: сбой декора деградирует в ту же заглушку, что показывалась во время
// загрузки. Страница остаётся живой. Всё, что НЕ является сбоем загрузки чанка,
// пробрасывается дальше — иначе оболочка глушила бы настоящие ошибки рендера.
//
// Та же механика нужна не только картинкам: отложенные глобальные слоты корня
// (`components/layout/deferred-globals.tsx`) держатся на ней же. Поэтому общая
// часть называется `ChunkSafe`, а `LazyArt` — её имя для иллюстраций. Второй
// реализации границы заводить не нужно: она одна на оба случая, отличается
// только заглушка и метка источника в отчёте.

import { Component, type ErrorInfo, type ReactNode, Suspense } from "react";
import { isChunkLoadError } from "@/lib/chunk-load";
import { captureClientError } from "@/lib/observability/hawk-client";

interface Props {
	/** Заглушка: и на время загрузки, и вместо не доехавшего чанка. */
	fallback: ReactNode;
	/** Метка источника в отчёте об ошибке — по ней в Hawk видно, что не доехало. */
	source?: string;
	children: ReactNode;
}

class ArtErrorBoundary extends Component<Props, { error: Error | null }> {
	state: { error: Error | null } = { error: null };

	static getDerivedStateFromError(error: Error): { error: Error } {
		return { error };
	}

	componentDidCatch(error: Error, info: ErrorInfo): void {
		if (!isChunkLoadError(error)) return;
		captureClientError(error, {
			source: this.props.source ?? "lazy-art",
			componentStack: info.componentStack ?? undefined,
		});
	}

	render(): ReactNode {
		const { error } = this.state;
		if (error) {
			if (!isChunkLoadError(error)) throw error;
			return this.props.fallback;
		}
		return this.props.children;
	}
}

/**
 * Граница + Suspense вокруг ленивого чанка: не доехал — на его месте остаётся
 * `fallback`, остальная страница живёт дальше.
 */
export function ChunkSafe({ fallback, source, children }: Props): React.ReactElement {
	return (
		<ArtErrorBoundary fallback={fallback} source={source}>
			<Suspense fallback={fallback}>{children}</Suspense>
		</ArtErrorBoundary>
	);
}

/** Имя `ChunkSafe` для декоративной иллюстрации публичной страницы. */
export function LazyArt({ fallback, children }: Props): React.ReactElement {
	return <ChunkSafe fallback={fallback}>{children}</ChunkSafe>;
}
